import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

export type RejectionSignal =
  | "Skill-Gap"
  | "Experience-Level"
  | "Culture-Fit"
  | "Location"
  | "Compensation"
  | "Position-Filled"
  | "Unspecified";

export type RejectionInsight = {
  signals: RejectionSignal[];
  primarySignal: RejectionSignal;
  reasons: string[];
  suggestedSkills: Array<{
    name: string;
    rationale: string;
  }>;
  summary: string;
};

const REJECTION_TOOL = {
  name: "save_rejection_insight",
  description:
    "Classify a job rejection reason into structured signals and suggest concrete improvements.",
  parameters: {
    type: "object",
    properties: {
      signals: {
        type: "array",
        items: {
          type: "string",
          enum: [
            "Skill-Gap",
            "Experience-Level",
            "Culture-Fit",
            "Location",
            "Compensation",
            "Position-Filled",
            "Unspecified",
          ],
        },
        description: "All signals detected in the rejection reason.",
      },
      primarySignal: {
        type: "string",
        enum: [
          "Skill-Gap",
          "Experience-Level",
          "Culture-Fit",
          "Location",
          "Compensation",
          "Position-Filled",
          "Unspecified",
        ],
      },
      reasons: {
        type: "array",
        items: { type: "string" },
        description: "Plain-English bullet points explaining the rejection.",
      },
      suggestedSkills: {
        type: "array",
        items: {
          type: "object",
          properties: {
            name: { type: "string" },
            rationale: { type: "string" },
          },
          required: ["name", "rationale"],
          additionalProperties: false,
        },
        description:
          "Up to 3 concrete skills the candidate should add. Each with a one-sentence rationale.",
      },
      summary: {
        type: "string",
        description: "1-2 sentence summary of the takeaway.",
      },
    },
    required: ["signals", "primarySignal", "reasons", "suggestedSkills", "summary"],
    additionalProperties: false,
  },
};

const STAGE_FOCUS: Record<string, string> = {
  recruiter:
    "Rejected after RECRUITER SCREEN — focus diagnosis on resume keywords, comp expectations, basic role/location fit, and how the candidate's headline/title reads. Skill suggestions should be high-visibility additions to the resume itself.",
  hiring_manager:
    "Rejected after HIRING MANAGER round — focus on role-depth storytelling, behavioural STAR weakness, motivation/why-this-company, and any signal that the candidate's experience didn't map to the JD's day-to-day. Suggest skills/experiences that would make the role-fit narrative tighter.",
  portfolio:
    "Rejected after PORTFOLIO / CASE round — focus on portfolio storytelling, depth of trade-off explanation, measurement of impact, and missing technical skills exposed during the walkthrough. Suggest skills that would deepen the portfolio rather than resume keywords.",
  panel:
    "Rejected after PANEL round — focus on cross-functional collaboration, influence without authority, handling disagreement, and stakeholder navigation. Skill gaps here are usually soft/process skills (e.g. 'Stakeholder management', 'Async collaboration').",
  leadership:
    "Rejected after LEADERSHIP / EXEC round — focus on strategic framing, business-impact articulation, vision, and resource-allocation reasoning. Skill suggestions should be strategic/leadership capabilities, not tactical tools.",
  offer:
    "Rejected during OFFER stage — focus on negotiation dynamics, comp/scope mismatch, references, or late-stage red flags. Suggested skills should be negotiation/career-strategy oriented rather than technical.",
};

export const classifyRejection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      reason: string;
      company?: string;
      role?: string;
      applicationId?: string;
      stageKey?: string | null;
    }) => {
      if (!input?.reason || input.reason.trim().length < 5)
        throw new Error("reason is required (min 5 chars)");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    try {
      await enforceQuota(supabase, userId, CAPABILITIES.rejection_insight);
    } catch (e) {
      if (e instanceof QuotaExceededError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    const { data: candidateRow } = await supabase
      .from("candidate_profiles")
      .select("verified_skills")
      .eq("user_id", userId)
      .maybeSingle();

    const currentSkills = (candidateRow?.verified_skills as Array<{ name: string }> | null)
      ?.map((s) => s.name)
      ?.join(", ") ?? "(none)";

    const stageGuidance = data.stageKey ? STAGE_FOCUS[data.stageKey] : null;

    let result: RejectionInsight;
    try {
      result = await callAIStructured<RejectionInsight>({
        messages: [
          {
            role: "system",
            content:
              "You are a recruiting analyst. Classify the rejection reason into structured signals. For Skill-Gap signals, suggest up to 3 concrete skills the candidate doesn't already have that would address the rejection. Skills should be canonical names (e.g. 'Kubernetes', 'GraphQL'). Do not suggest skills the candidate already lists." +
              (stageGuidance ? `\n\nROUND-SPECIFIC GUIDANCE:\n${stageGuidance}` : ""),
          },
          {
            role: "user",
            content: `Rejection context:\nCompany: ${data.company ?? "(unknown)"}\nRole: ${data.role ?? "(unknown)"}\nRejected at stage: ${data.stageKey ?? "(unknown)"}\nReason: ${data.reason}\n\nCandidate's current verified skills: ${currentSkills}`,
          },
        ],
        tool: REJECTION_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    await supabase.from("rejection_insights").insert({
      user_id: userId,
      application_id: data.applicationId ?? null,
      reasons: result.reasons as unknown as never,
      suggestions: result.suggestedSkills as unknown as never,
    });

    await incrementUsage(userId, CAPABILITIES.rejection_insight);

    return result;
  });
